<script setup lang="ts">
import { useMenuBuilder } from "@/composables/useMenuBuilder"
import { useNavbarState } from "@/stores"
import { useLocaleChooser } from "@/stores/useLocaleChooser"
import { useApp, useAppConfig, useToggleableValue } from "@/vf"
import { storeToRefs } from "pinia"
import { computed, watch } from "vue"
import RotateChevron from "../RotateChevron.vue"
import VarsityContentProcessor from "../VarsityContentProcessor.vue"
import DismissableBackdrop from "./DismissableBackdrop.vue"
import VarsityMobileSecondLevelMenu from "./VarsityMobileSecondLevelMenu.vue"

const navbarState = useNavbarState()
const { showMobileSidebar } = storeToRefs(navbarState)
const { toggleMobileSidebar } = navbarState

const app = useApp()
const { appConfig } = useAppConfig()
const localeChooser = useLocaleChooser()
const { currentLocale, showFakeLocale } = storeToRefs(localeChooser)
const { switchLocale, fakeLocale } = localeChooser

const {
    menuItems,
    myOfficeRoute,
    myAccountingRoute,
    myAdminRoute,
    myStaffRoute,
    myScoringRoute,
    getFirstChildRoutingData,
    getRootMenu,
} = useMenuBuilder()

const myVarsityMenu = computed(() => getRootMenu("my-varsity"))
const myOfficeMenu = computed(() => getRootMenu("my-office"))
const myAccountingMenu = computed(() => getRootMenu("my-accounting"))
const myAdminMenu = computed(() => getRootMenu("my-admin"))
const myStaffMenu = computed(() => getRootMenu("my-staff"))
const myScoringMenu = computed(() => getRootMenu("my-scoring"))

function close() {
    toggleMobileSidebar(false)
}

function logout() {
    close()
    app.logout()
}

function login() {
    navbarState.openCard("login")
    close()
}

/*─────────────────────────────────────┐
│  submenu                             │
└─────────────────────────────────────*/
const [openSubmenu, toggleSubmenu] = useToggleableValue<string>(null)
watch(showMobileSidebar, () => {
    openSubmenu.value = null

    for (const item of menuItems.value) {
        if (item.hasActiveChildren) {
            openSubmenu.value = item.id
            break
        }
    }
})
</script>

<template>
    <div class="mobile-sidebar" :class="{ active: showMobileSidebar }">
        <template v-for="item of menuItems">
            <div v-if="!item.settings.hideInRootNavigation" class="nav-item">
                <div class="d-flex align-items-center">
                    <RouterLink
                        :to="getFirstChildRoutingData(item)"
                        class="nav-link flex-grow-1"
                        @click="close"
                        :class="{ active: item.hasActiveChildren }"
                    >
                        <VarsityContentProcessor :content="$t(item.label)"></VarsityContentProcessor>
                    </RouterLink>
                    <div>
                        <RotateChevron :open="openSubmenu === item.id" @click.stop="toggleSubmenu(item.id)" />
                    </div>
                </div>

                <VarsityMobileSecondLevelMenu :item="item" :active="openSubmenu === item.id" @close="close" />
            </div>
        </template>

        <template v-if="appConfig.login">
            <hr />
            <h6>My Varsity EU</h6>

            <template
                v-for="menu of [
                    { label: 'My Profile', root: myVarsityMenu, link: '/my-varsity/my-profile' },
                    { label: 'My Staff', root: myStaffMenu, link: myStaffRoute },
                    { label: 'My Scoring', root: myScoringMenu, link: myScoringRoute },
                    { label: 'My Office', root: myOfficeMenu, link: myOfficeRoute },
                    { label: 'My Accounting', root: myAccountingMenu, link: myAccountingRoute },
                    { label: 'My Admin', root: myAdminMenu, link: myAdminRoute },
                ]"
            >
                <template v-if="menu.root && menu.link">
                    <div class="nav-item">
                        <div class="d-flex align-items-center">
                            <RouterLink class="nav-link flex-grow-1" :to="menu.link" @click="close">
                                {{ menu.label }}
                            </RouterLink>
                            <div>
                                <RotateChevron
                                    :open="openSubmenu === menu.root.id"
                                    @click.stop="toggleSubmenu(menu.root.id)"
                                />
                            </div>
                        </div>
                        <VarsityMobileSecondLevelMenu
                            :item="menu.root"
                            :active="openSubmenu === menu.root.id"
                            @close="close"
                        />
                    </div>
                </template>
            </template>
        </template>

        <hr />
        <button
            :class="{ active: locale.iso == currentLocale }"
            @click="switchLocale(locale.iso, 'en')"
            class="locale-button"
            v-for="locale in appConfig.languages"
        >
            <i class="fa fa-fw fa-globe-europe mr-2"></i>
            {{ locale.name }}
        </button>
        <button
            :class="{ active: fakeLocale == currentLocale }"
            @click="switchLocale(fakeLocale, fakeLocale)"
            class="locale-button"
            v-if="showFakeLocale"
        >
            Language Keys
        </button>

        <hr />
        <button v-if="appConfig.login" @click="logout" class="btn btn-link">
            <i class="fa fa-fw fa-sign-out-alt"></i>
            Logout
        </button>
        <button v-else @click="login" class="btn btn-link btn-block m-0">
            <i class="fa fa-fw fa-sign-in-alt"></i>
            Login
        </button>
    </div>

    <DismissableBackdrop :active="showMobileSidebar" @close="toggleMobileSidebar(false)" />
</template>

<style scoped>
.mobile-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    width: 90vw;
    max-width: 300px;
    height: 100%;
    z-index: 99;
    transition: 300ms ease-in-out transform;
    transform: translateX(100vw);
    overflow-y: auto;
    overflow-x: hidden;

    background-color: #0b6fb9;
    color: #fff;
    padding: 2rem 0.5rem 2rem 2rem;
}

.mobile-sidebar :deep(.nav-link) {
    color: #ddd;
}

.mobile-sidebar :deep(.nav-link:hover) {
    color: #fff;
}

.mobile-sidebar :deep(.nav-link.active) {
    color: #0d2030;
}

.mobile-sidebar h6 {
    color: #ddd;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
}

.mobile-sidebar.active {
    transform: translateX(0);
}

.locale-button {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    color: #fff;
    border: none;
    background-color: transparent;
    padding: 0.5rem 1rem;
}

.locale-button.active {
    color: var(--primary-dark);
}

@media screen and (min-width: 992px) {
    .mobile-sidebar {
        display: none;
    }
}
</style>
